import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/context/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { CertificateCard } from "@/components/certificates/CertificateCard";
import { Card, CardContent } from "@/components/ui/card";
import { Award, Search, BookOpen, Filter, Calendar, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger 
} from "@/components/ui/tooltip";
import { formatCEU } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const Certificates = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [timeFilter, setTimeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [isDownloading, setIsDownloading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { t } = useTranslation();

  console.log('[CERTIFICATES-DEBUG] Component mounted/updated, user:', user?.id);

  const forceRefresh = useCallback(() => {
    console.log('[CERTIFICATES-DEBUG] Forcing certificates refresh');
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const { data: certificates = [], isError, error, isLoading, refetch } = useQuery({
    queryKey: ['certificates', user?.id, refreshTrigger],
    queryFn: async () => {
      console.log('[CERTIFICATES-DEBUG] Fetching certificates for user:', user?.id);
      
      if (!user?.id) {
        console.log('[CERTIFICATES-DEBUG] No user ID, returning empty array');
        return [];
      }
      
      try {
        console.log('[CERTIFICATES-DEBUG] Making Supabase query');
        
        const { data, error } = await supabase
          .from('certificates')
          .select(`
            id,
            issue_date,
            feedback_submitted,
            courses:course_id (
              title,
              hours,
              category,
              subject_area,
              state_requirements
            )
          `)
          .eq('user_id', user.id)
          .order('issue_date', { ascending: false });

        if (error) {
          console.error('[CERTIFICATES-ERROR] Supabase query error:', error);
          throw new Error(error.message);
        }

        console.log('[CERTIFICATES-DEBUG] Certificates fetched:', data);
        
        const processedData = data?.map(cert => ({
          id: cert.id,
          course_title: cert.courses?.title || 'Untitled Course',
          completion_date: cert.issue_date,
          hours: cert.courses?.hours || 0,
          category: cert.courses?.category || 'Professional Development',
          subject_area: cert.courses?.subject_area || 'General',
          state_requirements: cert.courses?.state_requirements || {},
          feedback_submitted: cert.feedback_submitted
        })) || [];
        
        console.log('[CERTIFICATES-DEBUG] Processed certificates data:', processedData);
        
        return processedData;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load certificates';
        console.error('[CERTIFICATES-ERROR] Certificate loading error:', err);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        throw err;
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: 10000,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      console.log('[CERTIFICATES-DEBUG] Auto-refreshing certificates data');
      refetch();
    }, 15000);
    
    return () => clearInterval(interval);
  }, [refetch]);

  useEffect(() => {
    if (isError && error instanceof Error) {
      toast({
        title: "Error",
        description: `Failed to load certificates: ${error.message}`,
        variant: "destructive",
      });
    }
  }, [isError, error, toast]);

  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = cert.course_title.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;

    const certDate = new Date(cert.completion_date);
    const now = new Date();
    switch (timeFilter) {
      case "month":
        return certDate >= new Date(now.setMonth(now.getMonth() - 1));
      case "year":
        return certDate >= new Date(now.setFullYear(now.getFullYear() - 1));
      default:
        return true;
    }
  });

  const sortedCertificates = [...filteredCertificates].sort((a, b) => {
    switch (sortBy) {
      case "date":
        return new Date(b.completion_date).getTime() - new Date(a.completion_date).getTime();
      case "hours":
        return b.hours - a.hours;
      case "title":
        return a.course_title.localeCompare(b.course_title);
      default:
        return 0;
    }
  });

  const totalCEUs = certificates.reduce((sum, cert) => sum + (cert.hours || 0), 0);
  const formattedTotalCEUs = formatCEU(totalCEUs);

  const handleCertificateView = async (certificateId: string) => {
    try {
      setIsDownloading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      forceRefresh();
      
      toast({
        title: "Coming Soon",
        description: "Certificate download feature is coming soon!",
      });
    } catch (err) {
      console.error('Error viewing certificate:', err);
      toast({
        title: "Error",
        description: "Failed to retrieve certificate. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="w-full py-1 px-1 animate-fade-in">
      <div className="page-header">
        <div className="flex items-center gap-2">
          <Award className="h-6 w-6 text-[#004161]" />
          <h1>{t('certificates.title')}</h1>
        </div>
        <p>{t('certificates.subtitle')}</p>
      </div>

      <div className="w-full space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Card className="w-fit bg-accent-subtle border-accent/20">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-sm font-section text-primary/70">{t('certificates.totalCEUs')}</p>
                <p className="text-2xl font-title font-bold text-primary">{formattedTotalCEUs}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-accent" />
            <Input
              placeholder={t('certificates.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full font-body"
            />
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Select value={timeFilter} onValueChange={setTimeFilter}>
                      <SelectTrigger className="w-full md:w-[150px] font-section">
                        <Calendar className="h-4 w-4 mr-2 text-accent" />
                        <SelectValue placeholder="Time period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="font-body">{t('certificates.timeFilter.allTime')}</SelectItem>
                        <SelectItem value="month" className="font-body">{t('certificates.timeFilter.lastMonth')}</SelectItem>
                        <SelectItem value="year" className="font-body">{t('certificates.timeFilter.lastYear')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-body">Filter by time period</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-full md:w-[150px] font-section">
                        <Filter className="h-4 w-4 mr-2 text-accent" />
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date" className="font-body">{t('certificates.sort.latest')}</SelectItem>
                        <SelectItem value="hours" className="font-body">{t('certificates.sort.hours')}</SelectItem>
                        <SelectItem value="title" className="font-body">{t('certificates.sort.title')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-body">Sort certificates</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <Separator className="my-4 bg-primary/10" />

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-sm font-body text-muted-foreground mt-2">{t('certificates.loading')}</p>
          </div>
        ) : sortedCertificates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedCertificates.map((certificate) => (
              <CertificateCard
                key={certificate.id}
                certificate={certificate}
                onView={() => handleCertificateView(certificate.id)}
              />
            ))}
          </div>
        ) : (
          <Card className="p-6 text-center border-accent-light/20 bg-accent-subtle/30">
            <CardContent className="space-y-3">
              <BookOpen className="h-8 w-8 mx-auto text-accent" />
              {searchQuery || timeFilter !== "all" ? (
                <>
                  <h3 className="font-heading font-medium text-primary">{t('certificates.empty.noMatching')}</h3>
                  <p className="text-sm font-body text-muted-foreground">
                    {t('certificates.empty.adjustFilters')}
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-heading font-medium text-primary">{t('certificates.empty.none')}</h3>
                  <p className="text-sm font-body text-muted-foreground">
                    {t('certificates.empty.earnCertificates')}
                  </p>
                  <Button asChild className="mt-4 bg-primary hover:bg-primary/90 text-white" size="sm">
                    <a href="/courses" className="font-body">{t('certificates.empty.browseCourses')}</a>
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Certificates;
